/**
 * Утилита для форматирования телефонных номеров
 */

/**
 * Форматирует один телефонный номер в формат +7 (XXX) XXX-XX-XX
 * 
 * @param {string} phonePart - Часть телефонного номера
 * @returns {string} - Отформатированный номер
 */
const formatSinglePhone = (phonePart) => {
    // Извлекаем дополнительные номера (доб., ext., добавочный и т.д.)
    const extensionMatch = phonePart.match(/\s*(?:доб|ext|добавочный|доп|доп\.|доб\.)\s*[:\-]?\s*(\d+)/i);
    const extension = extensionMatch ? extensionMatch[0].trim() : '';
    
    // Убираем дополнительные номера из основного номера для форматирования
    let phoneWithoutExtension = phonePart.replace(/\s*(?:доб|ext|добавочный|доп|доп\.|доб\.)\s*[:\-]?\s*\d+/i, '').trim();
    
    // Убираем все символы, кроме цифр и +
    let cleaned = phoneWithoutExtension.replace(/[^\d+]/g, '');
    
    // Если номер начинается с 8, заменяем на +7
    if (cleaned.startsWith('8') && cleaned.length >= 11) {
        cleaned = '+7' + cleaned.substring(1);
    }
    
    // Если номер не начинается с +7, но содержит 11 цифр и начинается с 7
    if (!cleaned.startsWith('+7') && cleaned.startsWith('7') && cleaned.length === 11) {
        cleaned = '+7' + cleaned.substring(1);
    }
    
    // Если номер начинается с +7
    if (cleaned.startsWith('+7')) {
        // Убираем +7 и получаем только цифры номера
        const digits = cleaned.substring(2).replace(/\D/g, '');
        
        // Проверяем, что осталось 10 цифр (российский номер)
        if (digits.length === 10) {
            const formatted = `+7 (${digits.substring(0, 3)}) ${digits.substring(3, 6)}-${digits.substring(6, 8)}-${digits.substring(8, 10)}`;
            return extension ? `${formatted} ${extension}` : formatted;
        }
        
        // Если больше 10 цифр, пытаемся извлечь основной номер
        if (digits.length > 10) {
            // Берем первые 10 цифр
            const mainDigits = digits.substring(0, 10);
            const rest = digits.substring(10);
            const formatted = `+7 (${mainDigits.substring(0, 3)}) ${mainDigits.substring(3, 6)}-${mainDigits.substring(6, 8)}-${mainDigits.substring(8, 10)}`;
            const withRest = rest ? `${formatted} ${rest}` : formatted;
            return extension ? `${withRest} ${extension}` : withRest;
        }
    }
    
    // Если не удалось распознать как российский номер, возвращаем исходное значение
    return phonePart;
};

/**
 * Форматирует телефонный номер в формат +7 (XXX) XXX-XX-XX
 * Поддерживает различные форматы ввода: +7XXXXXXXXXX, 8XXXXXXXXXX, +7 (XXX) XXX-XX-XX и т.д.
 * Сохраняет дополнительные номера (доб.XXX, ext.XXX и т.п.)
 * Обрабатывает случаи с несколькими номерами через запятую
 * 
 * @param {string} phone - Телефонный номер в любом формате
 * @returns {string} - Отформатированный номер в формате +7 (XXX) XXX-XX-XX или исходное значение, если не удалось распознать
 */
export const formatPhoneNumber = (phone) => {
    if (!phone || typeof phone !== 'string') {
        return phone || '';
    }

    // Если номер содержит запятую, форматируем каждую часть отдельно
    if (phone.includes(',')) {
        const parts = phone.split(',').map(part => part.trim()).filter(part => part);
        return parts.map(part => formatSinglePhone(part)).join(', ');
    }
    
    return formatSinglePhone(phone);
};

/**
 * Проверяет, является ли значение телефонным номером
 * 
 * @param {string} value - Значение для проверки
 * @returns {boolean} - true, если значение похоже на телефонный номер
 */
export const isPhoneNumber = (value) => {
    if (!value || typeof value !== 'string') {
        return false;
    }
    
    // Убираем все символы, кроме цифр и +
    const cleaned = value.replace(/[^\d+]/g, '');
    
    // Проверяем, что есть хотя бы 10 цифр и номер начинается с +7, 7 или 8
    return (cleaned.startsWith('+7') || cleaned.startsWith('7') || cleaned.startsWith('8')) && 
           cleaned.replace(/\D/g, '').length >= 10;
};




// WEBPACK FOOTER //
// ./App/services/phoneFormatter.js